<template>
  <div
    class="notification-item"
    :class="{ 'notification-item--unread': !notification.isRead }"
    @click="handleClick"
  >
    <div class="notification-item__indicator">
      <div v-if="!notification.isRead" class="notification-item__dot" />
    </div>

    <div class="notification-item__icon">
      <component :is="iconComponent" :size="20" />
    </div>

    <div class="notification-item__content">
      <div class="notification-item__title">{{ notification.title }}</div>
      <div class="notification-item__body">{{ notification.body }}</div>
      <div class="notification-item__meta">
        <span class="notification-item__time">{{ formattedTime }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { BellIcon, EyeIcon, CalendarIcon, UsersIcon, FileTextIcon } from 'lucide-vue-next'
import type { Notification } from '../types/notifications.types'

const props = defineProps<{
  notification: Notification
}>()

const emit = defineEmits<{
  click: [notification: Notification]
}>()

const { t } = useI18n()

const iconComponent = computed(() => {
  switch (props.notification.notificationType) {
    case 'presentation_viewed':
      return EyeIcon
    case 'calendar_reminder':
      return CalendarIcon
    case 'agent_manager_assigned':
      return UsersIcon
    case 'company_verification_confirmed':
    case 'company_verification_needs_admission':
      return FileTextIcon
    default:
      return BellIcon
  }
})

const formattedTime = computed(() => {
  if (!props.notification.createdAt) return ''
  const date = new Date(props.notification.createdAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMin < 1) return t('notifications.time.justNow')
  if (diffMin < 60) return t('notifications.time.minutesAgo', { n: diffMin })
  if (diffHours < 24) return t('notifications.time.hoursAgo', { n: diffHours })
  if (diffDays < 7) return t('notifications.time.daysAgo', { n: diffDays })

  return date.toLocaleDateString()
})

function handleClick() {
  emit('click', props.notification)
}
</script>

<style scoped>
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid var(--color-border, #ecedf1);
}
.notification-item:hover {
  background: var(--color-background-soft, #fafafa);
}
.notification-item--unread {
  background: #f5f5ff;
}
.notification-item--unread:hover {
  background: #eef0ff;
}
.notification-item__indicator {
  width: 8px;
  flex-shrink: 0;
  padding-top: 6px;
}
.notification-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4f46e5;
}
.notification-item__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--color-background-mute, #f4f4f5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary, #52525b);
}
.notification-item--unread .notification-item__icon {
  background: #eef2ff;
  color: #4f46e5;
}
.notification-item__content {
  flex: 1;
  min-width: 0;
}
.notification-item__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #18181b;
  line-height: 1.3;
}
.notification-item__body {
  font-size: 0.8125rem;
  color: var(--color-text-secondary, #52525b);
  line-height: 1.4;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.notification-item__meta {
  margin-top: 4px;
}
.notification-item__time {
  font-size: 0.75rem;
  color: var(--color-text-tertiary, #a1a1aa);
}
</style>
